import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { filter, find, groupBy, includes, reduce } from 'lodash';
import { Observable } from 'rxjs';
import { unsubscribeComponent } from 'Src/ng2/shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { ISchool, TValidSchoolTypes } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { ITool } from 'Src/ng2/shared/typings/interfaces/tool.interface';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { ISidebarItem } from 'Src/nvps-libraries/design/nv-sidebar-list/nv-sidebar.interface';
import {
  SHEET_TYPE_HUMAN_MAP,
  SHEET_TYPE_TO_ROUTE,
  VIEWS_TO_STATES,
  IInternalView,
  hsViews,
  emsViews,
} from '../other-tools.constant';
import { SYNC_ENABLED_DOMAINS } from './../../school-tools-list/sync.constant';

interface INavItem {
  key: string;
  humanName: string;
  stateName?: string;
}
@unsubscribeComponent
@Component({
  selector: 'other-tools-left-nav',
  template: require('./other-tools-left-nav.component.html'),
  styles: [require('./other-tools-left-nav.component.scss')],
})
export class OtherToolsLeftNavComponent implements OnInit {
  @Input() tools$: Observable<ITool[]>;
  @Input() showSyncedSheets: boolean;
  @Input() currentSchool: ISchool;
  @Input() currentUser: IUser;
  @Output() updateView: EventEmitter<any> = new EventEmitter<any>();

  toolsSubscription;
  internalViews: IInternalView[];
  internalViewsSheetsMenu: ISidebarItem[];
  internalViewsToolsMenu: ISidebarItem[];
  sidebarTitleGS = 'Google Sheets';
  sidebarTitleTools = 'Tools';
  selectedSidebarItem: string;

  userMenuOptions = [
    {
      key: 'reportIssues',
      humanName: 'Report Issues',
      stateName: null,
    },
    {
      key: 'privacyAndTerms',
      humanName: 'Privacy & Terms',
      stateName: 'privacy-and-terms',
    },
  ];

  public routeOptions: string[];
  public showAssessments: boolean;

  constructor(@Inject('$state') private $state, @Inject('$window') public $window: any, private imUser: ImUser) {}

  ngOnInit() {
    this.toolsSubscription = this.tools$.subscribe(tools => {
      const groupedTools = groupBy(tools, (tool: ITool) => {
        return tool.metaData.sheetToolType;
      });
      this.internalViews = this.getInternalViewsUserCanAccess(this.currentUser);
      this.filterMenuOptions(this.internalViews, groupedTools);
    });
  }

  filterMenuOptions(internalViews, groupedTools) {
    const canAccessSync = this.userCanAccessSync(this.currentUser);
    const { sheetsMenu, toolsMenu } = reduce(
      internalViews,
      (res, internalView) => {
        const { label } = internalView;
        const humanLabel = SHEET_TYPE_HUMAN_MAP[internalView.label];
        const sheetsExist = groupedTools[humanLabel];
        const sidebarItem = this.convertInternalViewToISidebarItem(internalView);
        if (label === 'Next term planning' || label === 'Program changes' || label === 'Resources') {
          res.toolsMenu = [...res.toolsMenu, sidebarItem];
        } else if (label === 'Synced sheets') {
          if (canAccessSync) res.sheetsMenu = [...res.sheetsMenu, sidebarItem];
        } else if (sheetsExist) {
          res.sheetsMenu = [...res.sheetsMenu, sidebarItem];
        }
        return res;
      },
      { sheetsMenu: [], toolsMenu: [] },
    );
    this.internalViewsSheetsMenu = sheetsMenu;
    this.internalViewsToolsMenu = toolsMenu;
    this.selectedSidebarItem = sheetsMenu.length ? sheetsMenu[0].key : toolsMenu[0].key;
  }

  convertInternalViewToISidebarItem(internalView: IInternalView): ISidebarItem {
    const { label } = internalView;
    return {
      human: label,
      key: label,
    };
  }

  goToMyStudents(): void {
    this.$state.go('main.school.home');
  }

  public updateViewGS(option): void {
    const route = SHEET_TYPE_TO_ROUTE[option];
    this.selectedSidebarItem = option;
    this.$state.go('main.school.other-tools.default', { currentSelection: route });
  }

  public updateViewTools(option): void {
    this.selectedSidebarItem = option;
    this.$state.go(VIEWS_TO_STATES[option]);
  }

  goInternal(stateLabel: string): void {
    const chosenView = find(hsViews, view => {
      return view.label === stateLabel;
    });
    this.$state.go(chosenView.state);
  }

  goToState(option: INavItem): void {
    switch (option.key) {
      case 'reportIssues':
        this.$window.FreshWidget.show();
        break;
      case 'privacyAndTerms':
        const url = this.$state.href(option.stateName);
        this.$window.open(url, '_blank');
        break;
      default:
        return null;
    }
  }

  getInternalViewsForSchoolType(schoolType: TValidSchoolTypes): IInternalView[] {
    const validHighSchoolTypes: TValidSchoolTypes[] = ['Small HS', 'Large HS', '6 to 12', 'Transfer'];
    const currentSchoolIsHs = includes(validHighSchoolTypes, schoolType);
    if (currentSchoolIsHs) return hsViews;
    else return emsViews;
  }

  getInternalViewsUserCanAccess(currentUser: IUser): any {
    const internalViewsForSchoolType = this.getInternalViewsForSchoolType(this.currentSchool.schoolType);
    return filter(internalViewsForSchoolType, (view: any) => {
      const state = view.state;
      if (state) {
        const userCanAccessState = this.imUser.canAccessState(currentUser, state);
        if (userCanAccessState) {
          return view;
        }
      }
    });
  }

  userCanAccessSync(currentUser: IUser): boolean {
    const isAdmin = this.imUser.canAccessSync(currentUser);
    const hasGafeEmail = !!this.imUser.getGafeEmail(currentUser);
    const gafeEmailDomain = this.imUser.getGafeEmailDomain(currentUser);
    const domainIsSyncEnabled = includes(SYNC_ENABLED_DOMAINS, gafeEmailDomain);
    return isAdmin && hasGafeEmail && domainIsSyncEnabled;
  }
}
